/**
 * Calendar & Diary — shared kinds, date math, and grouping.
 *
 * Wave 2 extracted these from the wave-1 dashboard widget so the widget and
 * the diary page draw from one source: per-kind icons live next to the
 * KIND_COLORS in @/api/calendar, day/week/month grid math is pure and
 * testable, and groupEventsByDay backs both the widget's Upcoming list and
 * the diary's Schedule view.
 */
import {
  Calendar as CalendarGlyph,
  Eye,
  ListTodo,
  Phone,
  Repeat,
  Users,
} from 'lucide-react'
import { KIND_COLORS, type CalendarEvent, type CalendarKind } from '@/api/calendar'
import { cn } from '@/lib/utils'
import { t, type DashboardLocale } from '@/pages/agent/dashboard/copy'

export { KIND_COLORS }

export const CALENDAR_KINDS: CalendarKind[] = ['viewing', 'meeting', 'todo', 'call', 'custom']

export const KIND_ICONS: Record<CalendarKind, typeof Eye> = {
  viewing: Eye,
  meeting: Users,
  todo: ListTodo,
  call: Phone,
  custom: CalendarGlyph,
}

/**
 * Wave 6b — the small Repeat affordance marking a recurring-series occurrence
 * (is_recurrence_instance). Non-interactive: a labelled, tooltipped glyph in
 * the IconAction title/label pattern.
 */
export function SeriesGlyph({ locale, className }: { locale: DashboardLocale; className?: string }) {
  const label = t('calendar.series.occurrence', locale)
  return (
    <span role="img" aria-label={label} title={label} className={cn('inline-flex shrink-0 items-center', className)}>
      <Repeat className="h-3 w-3" aria-hidden="true" />
    </span>
  )
}

/**
 * Localized weekday labels in the product's Monday-start order (index 0 =
 * Monday … index 6 = Sunday). The diary grids and the wave-4 settings
 * operating-hours rows share this so rows stay in the locale's week order;
 * convert a row index to the JS weekday with `jsWeekdayForRow`.
 */
export function weekdayLabelsFor(locale: string): string[] {
  // 2024-01-01 was a Monday.
  const base = new Date(2024, 0, 1)
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(base)
    d.setDate(base.getDate() + i)
    return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d)
  })
}

/** Row index (Monday-start) → JS weekday (0=Sunday..6=Saturday). */
export function jsWeekdayForRow(rowIndex: number): number {
  return (rowIndex + 1) % 7
}

export function startOfDay(d: Date): Date {
  const out = new Date(d)
  out.setHours(0, 0, 0, 0)
  return out
}

export function dayKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** Local wall-clock value for <input type="datetime-local">. */
export function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function resolvedTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/** Monday-start grid cells for a month; leading/trailing blanks are null. */
export function monthCellsFor(year: number, month: number): Array<{ day: number | null }> {
  const firstOffset = (new Date(year, month, 1).getDay() + 6) % 7
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const cells: Array<{ day: number | null }> = []
  for (let i = 0; i < firstOffset; i++) cells.push({ day: null })
  for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d })
  while (cells.length % 7 !== 0) cells.push({ day: null })
  return cells
}

/** The 7 days (Monday-start) of the week containing `anchor`. */
export function weekDaysFor(anchor: Date): Date[] {
  const start = startOfDay(anchor)
  const offset = (start.getDay() + 6) % 7
  start.setDate(start.getDate() - offset)
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    return d
  })
}

export interface FetchWindow {
  from: Date
  to: Date
}

/**
 * Visible range plus one view-width of margin on each side so pager moves
 * are instant — the target page is almost always already loaded.
 */
export function fetchWindowFor(view: 'day' | 'week' | 'month' | 'schedule', anchor: Date): FetchWindow {
  const a = startOfDay(anchor)
  if (view === 'day') {
    const from = new Date(a)
    from.setDate(from.getDate() - 1)
    const to = new Date(a)
    to.setDate(to.getDate() + 2)
    return { from, to }
  }
  if (view === 'week') {
    const days = weekDaysFor(a)
    const from = new Date(days[0])
    from.setDate(from.getDate() - 7)
    const to = new Date(days[6])
    to.setDate(to.getDate() + 7)
    return { from, to }
  }
  if (view === 'month') {
    const first = new Date(a.getFullYear(), a.getMonth(), 1)
    const last = new Date(a.getFullYear(), a.getMonth() + 1, 0)
    const from = new Date(first)
    from.setDate(from.getDate() - 7)
    const to = new Date(last)
    to.setDate(to.getDate() + 7)
    return { from, to }
  }
  // Schedule: the Upcoming window at full width, padded one week back so a
  // just-pasted event still renders.
  const from = new Date(a)
  from.setDate(from.getDate() - 7)
  const to = new Date(a)
  to.setDate(to.getDate() + 60)
  return { from, to }
}

/**
 * Date-grouped buckets, sorted by day then start time. Callers apply their
 * own window filter first (the widget keeps its 30-day Upcoming window; the
 * diary filters by the visible range) — grouping itself is shared.
 */
export function groupEventsByDay(events: CalendarEvent[]): Array<[string, CalendarEvent[]]> {
  const sorted = [...events]
    .filter((e) => !Number.isNaN(new Date(e.starts_at).getTime()))
    .sort((a, b) => (a.starts_at < b.starts_at ? -1 : a.starts_at > b.starts_at ? 1 : 0))
  const groups = new Map<string, CalendarEvent[]>()
  for (const event of sorted) {
    const key = dayKey(new Date(event.starts_at))
    const bucket = groups.get(key)
    if (bucket) bucket.push(event)
    else groups.set(key, [event])
  }
  return [...groups.entries()]
}

/** Events whose start falls on the given day key. */
export function eventsOnDay(events: CalendarEvent[], key: string): CalendarEvent[] {
  return events.filter((e) => dayKey(new Date(e.starts_at)) === key)
}

/** True when the event spans more than one calendar day (renders as a chip). */
export function isMultiDay(event: CalendarEvent): boolean {
  if (!event.ends_at) return false
  const start = new Date(event.starts_at)
  const end = new Date(event.ends_at)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false
  return dayKey(start) !== dayKey(end)
}
